import React from 'react'
import type { FfpDashboardData } from '../../types'
import styles from './engagement.module.sass'
import type { Channel, EngagementPoint } from './utils'

// What the engagement, coaching and app cards each take.
export type EngagementCardProps = {
  data: FfpDashboardData
  points: EngagementPoint[]
  since: string
  footer: string
  highlighted: boolean
  onAbout: () => void
}

export const SmallPrint = ({ text }: { text: string | null }) => {
  if (!text) return null
  return <p className={styles.smallPrint}>{text}</p>
}

type FigureProps = {
  value: string
  label: string
  change: string | null
}

// The big share, what it counts and how it has moved.
export const Figure = ({ value, label, change }: FigureProps) => (
  <div className={styles.figure}>
    <span className={styles.big}>{value}</span>
    <div className={styles.figureText}>
      <span className={styles.bigLabel}>{label}</span>
      <Badge text={change} />
    </div>
  </div>
)

const Badge = ({ text }: { text: string | null }) => {
  if (!text) return null
  return <span className={styles.badge}>{text}</span>
}

const FILL_CLASS: Record<Channel, string> = {
  onboarded: styles.fillOnboarded,
  coaching: styles.fillCoaching,
  workshops: styles.fillWorkshops,
  app: styles.fillApp,
}

// Past this the share sits inside the bar instead of after it.
const LABEL_INSIDE_FROM = 85

type ShareBarProps = { share: number; channel: Channel }

// A channel's share of enrolled people, against 0 to 100%.
export const ShareBar = ({ share, channel }: ShareBarProps) => (
  <div className={styles.shareBar}>
    <div className={styles.track}>
      <BarFill share={share} channel={channel} />
    </div>
    <div className={styles.axis} aria-hidden="true">
      <span>0% of enrolled</span>
      <span>50% of enrolled</span>
      <span>100% of enrolled</span>
    </div>
  </div>
)

const BarFill = ({ share, channel }: ShareBarProps) => {
  if (share === 0) return null

  const inside = share > LABEL_INSIDE_FROM
  const fillClass = inside ? `${styles.fill} ${styles.fillLabelled} ${FILL_CLASS[channel]}` : `${styles.fill} ${FILL_CLASS[channel]}`
  const label = <span className={styles.fillValue}>{`${share}%`}</span>

  return (
    <>
      <span className={fillClass} style={{ width: `${share}%` }}>{inside ? label : null}</span>
      {inside ? null : label}
    </>
  )
}
